'use client';

import React, { useState, useEffect, useMemo } from 'react';
import { Input } from './Input';
import { TextArea } from './TextArea';
import { Button } from '@/components/Button';
import { CityOption, SecondaryServices } from '@/types';
import ServiceSelect from './ServiceSelect';
import CitySelect from './CitySelect';
import { Modal } from '@/components/Modal';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { Location } from '@/context/location';
import { removeAccents } from '@/utils/removeAccents';

interface Props {
  citiesProp: Array<CityOption>;
  primaryServiceProp: string;
  secondaryServiceProp: Array<SecondaryServices>;
  cidadeProp?: string;
}

interface SelectedOption {
  value: number;
  label: string;
  labelSlug: string;
}

export function Orcamento({
  primaryServiceProp,
  secondaryServiceProp,
  citiesProp,
  cidadeProp,
}: Props) {
  const { cidadeLocation, requestLocation, locating } = Location();

  const pathname = usePathname();

  // @ts-ignore
  const secondaryService = pathname.split('/')[2];

  const { executeRecaptcha } = useGoogleReCaptcha();
  // @ts-ignore
  const term: string = secondaryService || '';

  const [selectedService, setSelectedService] = useState<SelectedOption[]>([]);
  const [serviceSimpleArray, setserviceSimpleArray] = useState<number[]>([]);
  const [serviceNameSimpleArray, setserviceNameSimpleArray] = useState<
    string[]
  >([]);

  const [cityInputValue, setCityInputValue] = useState('');

  const [detalhes, setDetalhes] = useState('');
  const [nome, setNome] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [cidade, setCidade] = useState('');
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [hasSent, setHasSent] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [servicesSelect, setServicesSelect] = useState<
    { value: number; label: string; labelSlug: string }[]
  >([]);
  const [cityOptions, setCityOptions] = useState<
    { value: string; label: string }[]
  >([]);

  useEffect(() => {
    const services = secondaryServiceProp.map((service) => ({
      value: service.Id,
      label: service.Name,
      labelSlug: service.SlugName,
    }));
    setServicesSelect(services);

    const cities = citiesProp.map((city) => ({
      value: city.Name,
      label: city.UFName,
    }));
    setCityOptions(cities);
  }, [secondaryServiceProp, citiesProp]);

  useEffect(() => {
    if (term) {
      const filteredItems = servicesSelect.filter(
        (item) => item.labelSlug === term
      );
      handleSelectChange(filteredItems);
    }
  }, [servicesSelect]);

  const handleSelectChange = (selectedOptions: SelectedOption[]) => {
    const selectedValuesSimpleArray: number[] = selectedOptions.map(
      (option) => option.value
    );
    const selectedNameValuesSimpleArray: string[] = selectedOptions.map(
      (option) => option.label
    );
    setserviceSimpleArray(selectedValuesSimpleArray);
    setserviceNameSimpleArray(selectedNameValuesSimpleArray);
    setSelectedService(selectedOptions);
  };

  const handleCityChange = (selectedCity: { label: string }) => {
    setCidade(selectedCity.label);
  };

  const handleCityInputChange = (inputValue: string) => {
    setCityInputValue(inputValue);
  };

  const handleNomeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNome(e.target.value);
  };

  const handleWhatsappChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    const numericValue = inputValue.replace(/\D/g, '');

    let maskedValue = '';
    if (numericValue.length <= 2) {
      maskedValue = numericValue;
    } else if (numericValue.length <= 7) {
      maskedValue = `(${numericValue.slice(0, 2)}) ${numericValue.slice(2)}`;
    } else if (numericValue.length <= 11) {
      maskedValue = `(${numericValue.slice(0, 2)}) ${numericValue.slice(
        2,
        7
      )}-${numericValue.slice(7)}`;
    } else {
      maskedValue = `(${numericValue.slice(0, 2)}) ${numericValue.slice(
        2,
        7
      )}-${numericValue.slice(7, 11)}`;
    }

    setWhatsapp(maskedValue);
  };

  // Filtragem local e imediata (a lista já está em memória); limita as opções exibidas.
  const filteredCities = useMemo(() => {
    const query = removeAccents(cityInputValue.trim().toLowerCase());
    if (!query) return [];

    return cityOptions
      .filter((city) => removeAccents(city.label.toLowerCase()).includes(query))
      .slice(0, 50);
  }, [cityInputValue, cityOptions]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (loading || hasSent) return;

    setErrorMessage('');

    const numericWhatsapp = whatsapp.replace(/\D/g, '');

    if (serviceSimpleArray.length === 0) {
      setErrorMessage('Selecione ao menos um serviço.');
      return;
    }

    if (!cidade) {
      setErrorMessage('Selecione sua cidade.');
      return;
    }

    if (numericWhatsapp.length < 10 || numericWhatsapp.length > 11) {
      setErrorMessage('Informe um WhatsApp válido com DDD.');
      return;
    }

    if (!executeRecaptcha) {
      setErrorMessage(
        'Não foi possível validar a segurança do formulário. Recarregue a página e tente novamente.',
      );
      return;
    }

    setLoading(true);

    try {
      const token = await executeRecaptcha('onSubmit');

      const data = {
        PrimaryServiceName: primaryServiceProp,
        ServicesNames: serviceNameSimpleArray,
        ServicesIds: serviceSimpleArray,
        Detalhes: detalhes,
        Nome: nome,
        WhatsappUser: '55' + numericWhatsapp,
        Cidade: cidade,
        Limit: 999,
      };

      const response = await fetch('/api/sendOrcamentoApi', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data, token }),
      });

      if (!response.ok) {
        const body = await response.json().catch(() => null);
        setErrorMessage(
          body?.message ?? 'Não foi possível enviar seu orçamento. Tente novamente.',
        );
        return;
      }

      setShowModal(true);
      setHasSent(true);
    } catch (error) {
      console.error(error);
      setErrorMessage('Não foi possível enviar seu orçamento. Verifique sua conexão e tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className='bg-frame p-5 md:p-10'>
        <form className='w-full' onSubmit={handleSubmit}>
          {servicesSelect && (
            <ServiceSelect
              selectedService={selectedService}
              handleSelectChange={handleSelectChange}
              servicesSelect={servicesSelect}
            />
          )}

          <TextArea
            className='input-pattern my-2 w-full placeholder:text-[#565656]'
            required
            placeholder='Detalhes adicionais sobre o serviço'
            aria-label='Detalhes adicionais sobre o serviço'
            name='detalhes'
            icon='mdi-receipt-text-edit-outline'
            value={detalhes}
            onChange={(e) => setDetalhes(e.target.value)}
          />

          <div className='md:flex  md:justify-between'>
            <Input
              className='input-pattern my-2 w-full'
              id='orcamento-nome'
              type='text'
              required
              placeholder='Seu nome'
              aria-label='Seu nome'
              autoComplete='name'
              name='nome'
              icon='mdi-account-outline'
              value={nome}
              onChange={handleNomeChange}
            />
            <div className='my-2 flex h-[48px] place-items-center rounded bg-[white] text-center lg:ml-3'>
              <span className='shrink-0 pl-3 pr-1 text-[#565656]' aria-hidden>
                +55
              </span>
              <Input
                id='orcamento-whatsapp'
                type='tel'
                inputMode='tel'
                autoComplete='tel-national'
                required
                placeholder='Seu WhatsApp (com DDD)'
                aria-label='Seu WhatsApp, com DDD'
                name='whatsapp'
                value={whatsapp}
                onChange={handleWhatsappChange}
                className='border-none w-full !pl-2'
              />
            </div>
          </div>

          {cityOptions && (
            <CitySelect
              filteredCities={filteredCities}
              cityOptions={cityOptions}
              cidade={cidade}
              isLoadingCities={false}
              handleCityInputChange={handleCityInputChange}
              handleCityChange={handleCityChange}
              defaultValue={cidadeProp ?? cidadeLocation}
            />
          )}

          {!cidadeProp && (
            <button
              type='button'
              onClick={requestLocation}
              disabled={locating}
              className='my-1 text-sm text-primary underline'
            >
              {locating ? 'Localizando...' : 'Usar minha localização'}
            </button>
          )}

          <div className='my-2'>
            <input
              type='checkbox'
              id='concordo-termos-e-politicas'
              required
              className='mr-2 h-4 w-4'
            />
            <label
              htmlFor='concordo-termos-e-politicas'
              className='text-gray-light'
            >
              Estou ciente dos{' '}
              <Link
                href='/termos-e-politicas'
                target='_blank'
                className='underline'
              >
                termos e políticas
              </Link>
            </label>
          </div>

          <Button
            type='submit'
            disabled={loading || hasSent}
            className='input-pattern text-bg-secondary border-blue-700 my-2 flex w-full items-center justify-center rounded border px-4 py-2 text-center font-bold'
          >
            {loading ? 'Enviando...' : 'Enviar'}
          </Button>

          {errorMessage && (
            <p role='alert' className='my-1 text-sm font-bold text-[#FF4B4B]'>
              {errorMessage}
            </p>
          )}
        </form>
      </div>

      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        haveSvg={true}
        className='modal-orcamento'
      >
        <p className='mb-4 text-2xl font-bold'>Prontinho!</p>
        <p>
          Fica ligado no seu <b>WhatsApp</b>, que em breve você começará a
          receber orçamentos!
        </p>
      </Modal>
    </div>
  );
}
